/**
 * UID-611 — TEMPORARY, human ruling 2026-09-11 (demo purposes, revertable).
 *
 * ADR-0179 D2's ORIGINAL rule was that a quote-provenance RFQ line's unit price is
 * FROZEN and never re-discounted by the catalog volume-break curve — matching the cart's
 * own `isVolumeBreakExempt` treatment of quote/RFQ-sourced lines. Product asked,
 * explicitly and with the reversal named as intentional and temporary, for the curve to
 * apply to RFQ lines too — including on `accepted` RFQs — so a quantity edit visibly
 * reprices the line for demo purposes.
 *
 * The real curve lives ONLY in `apps/api/src/modules/cart/cart-derivation.ts`
 * (`VOLUME_BREAKS`), which cannot be imported into this bundle (backend-only module).
 * This is a DELIBERATE, DOCUMENTED, TEMPORARY duplicate of that table for DISPLAY
 * purposes only — never the source of truth for money. `apps/api/src/modules/rfq/
 * rfq-cost-estimate.ts` already applies the real curve server-side for the RFQ's
 * cost-estimate/quote-summary figures; this module exists only so the two frontend
 * displays that read the RAW stored quote price directly (`RfqCard.tsx`'s per-line
 * Unit price cell, `RfqDetailSummaryRail.tsx`'s Total) can show a NUMBER THAT AGREES
 * with what the backend now computes, without a round trip for every keystroke.
 *
 * TO REVERT (both the backend and this file): see `rfq-cost-estimate.ts`'s doc comment
 * for the one-line backend revert; delete this file and its two call sites, restoring
 * the frozen-unit-price display both callers had before this ruling.
 */

// Kept in byte-for-byte sync with `cart-derivation.ts`'s `VOLUME_BREAKS` BY HAND — see
// the module doc comment above for why this can't be a shared import. Do not add a
// rung here without adding the identical one there, or the two surfaces will disagree.
const VOLUME_BREAKS: ReadonlyArray<{ readonly minQuantity: number; readonly pctOff: number }> = [
  { minQuantity: 1, pctOff: 0 },
  { minQuantity: 10, pctOff: 5 },
  { minQuantity: 50, pctOff: 10 },
  { minQuantity: 100, pctOff: 15 },
]

function volumeBreakPctOffFor(quantity: number): number {
  let pctOff = 0
  for (const rung of VOLUME_BREAKS) {
    if (quantity >= rung.minQuantity) pctOff = rung.pctOff
  }
  return pctOff
}

/**
 * The TEMPORARILY-discounted per-unit price for `quantity`, as a decimal-string
 * amount at the SAME precision as `unitPriceAmount` (2dp, matching every other money
 * string this app renders via `formatMoney`). Rounds to the cent, same rung as the
 * backend's own `repriceLineScaled` (round the unit BEFORE any further multiply), so a
 * caller that also multiplies by quantity gets a subtotal that matches the backend's.
 */
export function previewDiscountedUnitPrice(unitPriceAmount: string, quantity: number): string {
  const pctOff = volumeBreakPctOffFor(quantity)
  if (pctOff === 0) return unitPriceAmount
  const discounted = Number(unitPriceAmount) * (1 - pctOff / 100)
  return discounted.toFixed(2)
}
